import React, { Component } from 'react'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import DocumentTitle from 'react-document-title'
import qs from 'qs'
import { showSectionSelectionModal } from '../../actions/modals'
import { updatePreviewItem } from '../../actions/previewItem'
import SearchProviderContainer from './SearchProviderContainer'
import Button from '../../components/uiKit/Button'
import TopPageHeader from '../../components/pageHeaders/TopPageHeader'
import StickyPageHeader from '../../components/pageHeaders/StickyPageHeader'
import SearchResultsCard from '../../components/search/SearchResultsCard'
import SearchResultsFolderCard from '../../components/search/SearchResultsFolderCard'
import SearchResultsRepoCard from '../../components/search/SearchResultsRepoCard'
import CategoryFilters, { CategoryHeadersToggle } from '../../components/search/CategoryFilters'
import SearchResultsPreviewPanel from '../../components/search/SearchResultsPreviewPanel'
import Icon from '../../components/misc/Icons'
import { getQuestionBodyLabel } from '../../utils/getQuestionBodyLabel'
import { getDocumentType } from '../../utils/search/getDocumentType'
import { getRepoforId } from '../../utils/getRepoforId'
import { getLinkForRepo } from '../../utils/getLinkForRepo'
import { useSearchContext } from '../../services/search/SearchContext'
import { recordRecentSearch } from '../../utils/search/recentSearches'
import analytics from '../../utils/cdpAnalytics'

const updateAfter = 700
const createURL = (state) => `?${qs.stringify({ query: state.query })}`
const searchStateToUrl = (props, searchState) => (
  searchState ? `${props.location.pathname}${createURL(searchState)}` : ''
)
const urlToSearchState = (location) => qs.parse(location.search.slice(1))

// ─── Search input ──────────────────────────────────────────────────────────────

function SearchInput() {
  const { currentRefinement, refine } = useSearchContext()
  return (
    <input
      type='text'
      placeholder='Search'
      value={currentRefinement}
      onChange={(e) => refine(e.target.value)}
      autoComplete='off'
      className='topBar-search'
      id='searchInputField'
      onKeyDown={(e) => {
        if (e.keyCode === 13) {
          // Any query committed directly on the results page is a content search.
          recordRecentSearch(currentRefinement)
          document.getElementById('searchInputField').blur()
        }
      }}
    />
  )
}

// ─── Router history bridge (react-router v4 — no useHistory) ──────────────────

const RouterHistoryContext = React.createContext({ history: { push: () => {} } })
const RouterHistoryBridge = withRouter(({ history, children }) => (
  <RouterHistoryContext.Provider value={{ history }}>
    {children}
  </RouterHistoryContext.Provider>
))
function useRouterHistory() {
  return React.useContext(RouterHistoryContext)
}

// ─── Category-filter context ──────────────────────────────────────────────────
// Lives on SearchResultsContainer's state. Descendants (the header pill bar
// and the per-category groups) subscribe via useCategoryFilters().

const DEFAULT_CATEGORY_FILTERS = {
  enabled: {
    repos: true, folders: true, sets: true, questions: true,
  },
  onToggle: () => {},
  showCategoryHeaders: true,
  onToggleCategoryHeaders: () => {},
}
const CategoryFiltersContext = React.createContext(DEFAULT_CATEGORY_FILTERS)
function useCategoryFilters() {
  return React.useContext(CategoryFiltersContext)
}

// ─── Active-result context ────────────────────────────────────────────────────
// Tracks which row in the (flattened) results list is currently active so
// keyboard navigation, the highlight class, and the right-column preview can
// stay in sync.

const ActiveResultContext = React.createContext({
  activeId: null,
  activeHit: null,
  flatHits: [],
  activateRow: () => {},
})
function useActiveResult() {
  return React.useContext(ActiveResultContext)
}

// ─── Section group header (Library) ───────────────────────────────────────────
// Rendered like the dropdown sectionHeader: icon on the left, uppercase label.

function SectionGroupHeader({ icon, label }) {
  return (
    <div className='searchResults-sectionGroupHeader'>
      <div className='searchResults-sectionGroupHeader-icon'>
        <Icon name={icon} />
      </div>
      <span className='searchResults-sectionGroupHeader-label'>{label}</span>
    </div>
  )
}

// ─── Individual hit renderer ───────────────────────────────────────────────────

// Stable id used in the flat-hits list and the active-row wrapper. `source`
// disambiguates the same logical item appearing across Algolia + local
// engines so each row in the visible list still has a unique key.
function buildHitRowId(source, hit) {
  const id = (hit && hit.objectID) || (hit && hit.id) || ''
  return `${source}-${id}`
}

// Highlight + auto-scroll wrapper around each result card.
function ActiveResultRow({ rowId, children }) {
  const { activeId } = useActiveResult()
  const ref = React.useRef(null)
  const isActive = activeId === rowId
  React.useEffect(() => {
    if (isActive && ref.current && typeof ref.current.scrollIntoView === 'function') {
      ref.current.scrollIntoView({ block: 'nearest' })
    }
  }, [isActive])
  return (
    <div
      ref={ref}
      className={`searchResults-rowWrapper${isActive ? ' searchResults-rowWrapper--active' : ''}`}
    >
      {children}
    </div>
  )
}

// Delay used to distinguish a single click from the first click of a double
// click — a single click is deferred this long and cancelled if a second click
// (double click) arrives.
const ROW_CLICK_DELAY = 220

const HitComponent = ({
  item, play, rowId, history, updatePreviewItem: dispatchUpdatePreviewItem,
}) => {
  const kind = getDocumentType(item)
  const { activateRow } = useActiveResult()
  const clickTimer = React.useRef(null)
  const itemId = item && (item.id || item._id)
  const isPreviewable = kind === 'set' || kind === 'question'

  const openDirect = React.useCallback(() => {
    const path = getPathForHit(kind, item)
    if (path) history.push(path)
  }, [kind, item, history])

  const handleClick = React.useCallback(() => {
    if (activateRow) activateRow(rowId)
    if (clickTimer.current) clearTimeout(clickTimer.current)
    clickTimer.current = setTimeout(() => {
      clickTimer.current = null
      // Single click: set/question open the lightweight preview modal; packs
      // and folders (no modal) navigate directly.
      if (isPreviewable && itemId) {
        dispatchUpdatePreviewItem(itemId)
      } else {
        openDirect()
      }
    }, ROW_CLICK_DELAY)
  }, [activateRow, rowId, isPreviewable, itemId, dispatchUpdatePreviewItem, openDirect])

  const handleDoubleClick = React.useCallback(() => {
    if (clickTimer.current) {
      clearTimeout(clickTimer.current)
      clickTimer.current = null
    }
    // Double click always opens the item.
    openDirect()
  }, [openDirect])

  React.useEffect(() => () => {
    if (clickTimer.current) clearTimeout(clickTimer.current)
  }, [])

  let card = null
  if (kind === 'set') {
    card = (
      <SearchResultsCard
        set
        item={item}
        title={item.name}
        play={() => { play(item) }}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
      />
    )
  } else if (kind === 'question') {
    card = (
      <SearchResultsCard
        question
        item={item}
        title={getQuestionBodyLabel(item)}
        play={() => { play(item) }}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
      />
    )
  } else if (kind === 'folder') {
    card = (
      <SearchResultsFolderCard
        folder={item}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
      />
    )
  } else if (kind === 'repo') {
    card = (
      <SearchResultsRepoCard
        repo={item}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
      />
    )
  }
  if (!card) return null
  return <ActiveResultRow rowId={rowId}>{card}</ActiveResultRow>
}

const Hit = withRouter(connect(null, { updatePreviewItem })(HitComponent))

function HitList({ hits, source, play }) {
  if (!hits || hits.length === 0) return null
  return (
    <div className='searchResults-list'>
      {hits.map((hit) => (
        <Hit
          item={hit}
          key={buildHitRowId(source, hit)}
          rowId={buildHitRowId(source, hit)}
          play={play}
        />
      ))}
    </div>
  )
}

// ─── Category header within Library (PACKS, FOLDERS, SETS, QUESTIONS) ─────────

function CategoryHeader({ label }) {
  return <div className='searchResults-categoryHeader'>{label}</div>
}

// ─── Generic library category ─────────────────────────────────────────────────
// Renders one category (e.g. "Packs") from Algolia results only. Local content
// engines (Fuse/MiniSearch) run as shadows and are not shown here.

function LibraryCategory({
  label,
  algoliaHits,
  algoliaHasMore,
  algoliaLoadMore,
  play,
}) {
  const { currentRefinement } = useSearchContext()
  const { showCategoryHeaders } = useCategoryFilters()
  if (!currentRefinement || algoliaHits.length === 0) return null
  return (
    <div className='searchResults-categoryGroup'>
      {showCategoryHeaders && <CategoryHeader label={label} />}
      <HitList hits={algoliaHits} source='a' play={play} />
      {algoliaHasMore && (
        <div className='searchResults-list-showMoreContainer'>
          <Button label='Show more results' size='large' onClickFunction={algoliaLoadMore} />
        </div>
      )}
    </div>
  )
}

// ─── Per-category wrappers ─────────────────────────────────────────────────────

function PacksGroup({ play }) {
  const { indexResults } = useSearchContext()
  const algo = indexResults.repos || {}
  return (
    <LibraryCategory
      label='Packs'
      algoliaHits={algo.hits || []}
      algoliaHasMore={!!algo.hasMore}
      algoliaLoadMore={algo.loadMore}
      play={play}
    />
  )
}

function FoldersGroup({ play }) {
  const { indexResults } = useSearchContext()
  const algo = indexResults.folders || {}
  return (
    <LibraryCategory
      label='Folders'
      algoliaHits={algo.hits || []}
      algoliaHasMore={!!algo.hasMore}
      algoliaLoadMore={algo.loadMore}
      play={play}
    />
  )
}

function SetsGroup({ play }) {
  const { indexResults } = useSearchContext()
  const algo = indexResults.sets_questions || {}
  const isSet = (h) => getDocumentType(h) === 'set'
  return (
    <LibraryCategory
      label='Sets'
      algoliaHits={(algo.hits || []).filter(isSet)}
      algoliaHasMore={false}
      algoliaLoadMore={null}
      play={play}
    />
  )
}

function QuestionsGroup({ play }) {
  const { indexResults } = useSearchContext()
  const algo = indexResults.sets_questions || {}
  const isQuestion = (h) => getDocumentType(h) === 'question'
  return (
    <LibraryCategory
      label='Questions'
      algoliaHits={(algo.hits || []).filter(isQuestion)}
      algoliaHasMore={false}
      algoliaLoadMore={null}
      play={play}
    />
  )
}

// ─── Library section ───────────────────────────────────────────────────────────

function LibrarySection({ play }) {
  const { indexResults, currentRefinement } = useSearchContext()
  const { enabled, showCategoryHeaders } = useCategoryFilters()
  const algoliaAny = (
    (indexResults.repos?.hitCount || 0) +
    (indexResults.folders?.hitCount || 0) +
    (indexResults.sets_questions?.hitCount || 0)
  ) > 0
  const anyEnabled = enabled.repos || enabled.folders || enabled.questions
  if (!currentRefinement || !algoliaAny || !anyEnabled) return null
  const libraryClass = showCategoryHeaders ?
    'searchResults-library' :
    'searchResults-library searchResults-library--continuous'
  return (
    <React.Fragment>
      <SectionGroupHeader icon='library' label='Library' />
      <div className={libraryClass}>
        {enabled.repos && <PacksGroup play={play} />}
        {enabled.folders && <FoldersGroup play={play} />}
        {enabled.sets && <SetsGroup play={play} />}
        {enabled.questions && <QuestionsGroup play={play} />}
      </div>
    </React.Fragment>
  )
}

// ─── Page header ──────────────────────────────────────────────────────────────

// Count how many `sets_questions` Algolia hits match a given kind.
function countSetsQuestionsByKind(indexResults, kind) {
  const isKind = (h) => getDocumentType(h) === kind
  const algoliaHits = (indexResults.sets_questions && indexResults.sets_questions.hits) || []
  return algoliaHits.filter(isKind).length
}

function countSimpleCategory(indexResults, indexName) {
  return (indexResults[indexName]?.hitCount) || 0
}

function ResultsPageHeader() {
  const { indexResults, currentRefinement } = useSearchContext()
  const {
    enabled, onToggle, showCategoryHeaders, onToggleCategoryHeaders,
  } = useCategoryFilters()

  const categoryCounts = {
    repos: countSimpleCategory(indexResults, 'repos'),
    folders: countSimpleCategory(indexResults, 'folders'),
    sets: countSetsQuestionsByKind(indexResults, 'set'),
    questions: countSetsQuestionsByKind(indexResults, 'question'),
  }
  // Content-only count: the page only renders Library (Algolia) content rows,
  // so the counter reflects exactly those. Local-only matches (e.g. navigation)
  // are intentionally excluded.
  const total = Object.keys(categoryCounts).reduce(
    (acc, key) => acc + (enabled[key] ? categoryCounts[key] : 0), 0,
  )

  let searchResultsText
  if (currentRefinement) {
    if (total === 1) searchResultsText = '1 Search Result'
    else if (total > 1) searchResultsText = `${total} Search Results`
    else searchResultsText = 'No Results'
  }

  const headerRow = currentRefinement ? (
    <div className='searchResults-headerRow'>
      <span className='searchResults-headerRow-count'>{searchResultsText}</span>
      <CategoryFilters enabled={enabled} onToggle={onToggle} />
      <CategoryHeadersToggle show={showCategoryHeaders} onToggle={onToggleCategoryHeaders} />
    </div>
  ) : searchResultsText

  return [
    <TopPageHeader
      key='topHeader'
      location='searchResults'
      header={<SearchInput />}
      headerInfoRow={headerRow}
    />,
    <StickyPageHeader
      key='stickyHeader'
      location='searchResults'
      header={currentRefinement}
      actionGroup={headerRow}
    />,
  ]
}

// ─── Flat-list builder + keyboard nav controller ─────────────────────────────

function getPathForHit(rowKind, hit) {
  if (rowKind === 'nav') return hit && hit.path
  if (rowKind === 'external') return hit && hit.url
  if (rowKind === 'repo') return getLinkForRepo(hit)
  if (rowKind === 'folder') {
    if (!hit) return null
    if (hit.repo) {
      const parentRepo = getRepoforId(hit.repo)
      if (parentRepo) return `${getLinkForRepo(parentRepo)}/${hit.id}`
      return `/packs/${hit.repo}/${hit.id}`
    }
    return `/library/${hit.id}`
  }
  if (rowKind === 'set') {
    const id = hit && (hit.id || hit._id)
    return id ? `/set/${id}` : null
  }
  if (rowKind === 'question') {
    const id = hit && (hit.id || hit._id)
    return id ? `/question/${id}` : null
  }
  return null
}

function appendLibraryRows(out, source, hits) {
  if (!Array.isArray(hits)) return
  hits.forEach((hit) => {
    const kind = getDocumentType(hit)
    if (!kind) return
    out.push({
      id: buildHitRowId(source, hit),
      kind,
      hit,
      path: getPathForHit(kind, hit),
      external: false,
    })
  })
}

// Builds the flat ordered list of currently-visible result rows, in the same
// order the page renders them: the Library categories in fixed order (Packs,
// Folders, Sets, Questions) — Algolia rows only. Mirrors LibrarySection so
// keyboard nav stays in sync.
function buildFlatHits(indexResults, enabled) {
  const out = []

  const algoIndex = (idx) => (indexResults[idx] && indexResults[idx].hits) || []
  const isSet = (h) => getDocumentType(h) === 'set'
  const isQuestion = (h) => getDocumentType(h) === 'question'

  if (enabled.repos) {
    appendLibraryRows(out, 'a', algoIndex('repos'))
  }
  if (enabled.folders) {
    appendLibraryRows(out, 'a', algoIndex('folders'))
  }
  if (enabled.sets) {
    appendLibraryRows(out, 'a', algoIndex('sets_questions').filter(isSet))
  }
  if (enabled.questions) {
    appendLibraryRows(out, 'a', algoIndex('sets_questions').filter(isQuestion))
  }

  return out
}

// True when the keyboard event happens inside an editable field (the search
// input, a contenteditable area, etc.) — in those cases ArrowUp/ArrowDown
// belong to the editor, not to the results list.
function isEditableTarget(target) {
  if (!target) return false
  const tag = (target.tagName || '').toUpperCase()
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true
  if (target.isContentEditable) return true
  return false
}

// react-redux v5 has no hooks, so the store-derived bits (previewItem flag +
// updatePreviewItem dispatcher) are injected via connect (see bottom of fn).
function ActiveResultProviderComponent({ children, previewItem, updatePreviewItem: dispatchUpdatePreviewItem }) {
  const { indexResults, currentRefinement } = useSearchContext()
  const { enabled } = useCategoryFilters()
  const { history } = useRouterHistory()
  // When the lightweight preview modal is open it owns the keyboard (arrows /
  // Esc / P / Q), so the results-page list handler must stand down.
  const isPreviewModalOpen = !!previewItem

  // When the query is empty there are no results to navigate or preview — force
  // an empty list so the right panel falls back to its empty state (otherwise a
  // stale active hit from the previous query keeps its preview mounted).
  const flatHits = React.useMemo(
    () => (currentRefinement ? buildFlatHits(indexResults, enabled) : []),
    [currentRefinement, indexResults, enabled],
  )

  const [activeIndex, setActiveIndex] = React.useState(0)

  // Reset to the first row whenever the query changes or the list shrinks.
  const flatIds = React.useMemo(() => flatHits.map((h) => h.id).join('|'), [flatHits])
  React.useEffect(() => {
    setActiveIndex(0)
  }, [currentRefinement, flatIds])

  // With an empty result set there are no rows to navigate or preview, so keep
  // the search input focused — the user is most likely still refining the query
  // (this also re-focuses after arriving from the dropdown, which blurs the
  // input on navigation so list arrow-keys can take over when results exist).
  const isEmptyResultSet = flatHits.length === 0
  React.useEffect(() => {
    if (!isEmptyResultSet) return
    const input = document.getElementById('searchInputField')
    if (input && document.activeElement !== input && typeof input.focus === 'function') {
      input.focus()
    }
  }, [isEmptyResultSet])

  const clampedIndex = flatHits.length === 0 ?
    -1 :
    Math.min(Math.max(activeIndex, 0), flatHits.length - 1)
  const activeRow = clampedIndex >= 0 ? flatHits[clampedIndex] : null

  React.useEffect(() => {
    function onKeyDown(e) {
      // The lightweight preview modal, when open, handles its own keys.
      if (isPreviewModalOpen) return
      // Allow Shift+Enter (opens the preview modal); block other Ctrl/Meta combos.
      const isShiftEnter = e.shiftKey && (e.key === 'Enter' || e.keyCode === 13)
      if (!isShiftEnter && (e.altKey || e.ctrlKey || e.metaKey)) return
      // ArrowDown while the search input is focused → move focus into the list
      // (re-focus the first row).
      const isSearchInput = e.target && e.target.id === 'searchInputField'
      if (isSearchInput && e.key === 'ArrowDown') {
        if (flatHits.length === 0) return
        e.preventDefault()
        if (typeof e.target.blur === 'function') e.target.blur()
        setActiveIndex(0)
        return
      }
      if (isEditableTarget(e.target) && e.key !== 'Enter') return
      if (flatHits.length === 0) return
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setActiveIndex((prev) => Math.min(prev + 1, flatHits.length - 1))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        // ArrowUp from the first row → focus the search input.
        if (activeIndex <= 0) {
          const input = document.getElementById('searchInputField')
          if (input && typeof input.focus === 'function') input.focus()
          return
        }
        setActiveIndex((prev) => Math.max(prev - 1, 0))
      } else if (e.key === 'Enter') {
        // The search input also listens for Enter to blur itself — only
        // hijack when the user isn't typing into it.
        if (isEditableTarget(e.target)) return
        const row = flatHits[Math.min(Math.max(activeIndex, 0), flatHits.length - 1)]
        if (!row) return
        const itemId = row.hit && (row.hit.id || row.hit._id)
        const isPreviewable = row.kind === 'set' || row.kind === 'question'
        // Match the dropdown: Shift+Enter opens / navigates.
        if (isShiftEnter) {
          if (!row.path) return
          e.preventDefault()
          if (row.external) {
            window.open(row.path, '_blank', 'noopener,noreferrer')
          } else {
            history.push(row.path)
          }
          return
        }
        // Plain Enter → lightweight preview modal for local set/question rows;
        // for non-previewable rows (packs/folders/external) it opens/navigates.
        if (isPreviewable && itemId) {
          e.preventDefault()
          dispatchUpdatePreviewItem(itemId)
          return
        }
        if (!row.path) return
        e.preventDefault()
        if (row.external) {
          window.open(row.path, '_blank', 'noopener,noreferrer')
        } else {
          history.push(row.path)
        }
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [flatHits, activeIndex, history, dispatchUpdatePreviewItem, isPreviewModalOpen])

  // Lets a mouse click on a row sync the active highlight (and therefore the
  // right-hand preview) to that row.
  const activateRow = React.useCallback((rowId) => {
    const idx = flatHits.findIndex((h) => h.id === rowId)
    if (idx >= 0) setActiveIndex(idx)
  }, [flatHits])

  const value = React.useMemo(() => ({
    activeId: activeRow ? activeRow.id : null,
    activeHit: activeRow ? activeRow.hit : null,
    activeKind: activeRow ? activeRow.kind : null,
    flatHits,
    activateRow,
  }), [activeRow, flatHits, activateRow])

  return (
    <ActiveResultContext.Provider value={value}>
      {children}
    </ActiveResultContext.Provider>
  )
}

const ActiveResultProvider = connect(
  (state) => ({ previewItem: state.previewItem }),
  { updatePreviewItem },
)(ActiveResultProviderComponent)

// Reads the active hit and feeds it to the right-column preview. Lives here
// (not in the JSX shell) because it needs access to the search contexts.
function ResultsRightPanel() {
  const { activeHit, activeKind } = useActiveResult()
  return <SearchResultsPreviewPanel activeHit={activeHit} activeKind={activeKind} />
}

// ─── SearchResultsContainer ───────────────────────────────────────────────────

class SearchResultsContainer extends Component {
  constructor(props) {
    super(props)
    this.onSearchStateChange = this.onSearchStateChange.bind(this)
    this.toggleCategory = this.toggleCategory.bind(this)
    this.toggleCategoryHeaders = this.toggleCategoryHeaders.bind(this)
    this.state = {
      searchState: urlToSearchState(props.location),
      enabledCategories: {
        repos: true, folders: true, sets: true, questions: true,
      },
      showCategoryHeaders: true,
    }
  }

  componentDidMount() {
    analytics.page('Search Results')
  }

  UNSAFE_componentWillReceiveProps(props) {
    if (props.location !== this.props.location) {
      this.setState({ searchState: urlToSearchState(props.location) })
    }
  }

  onSearchStateChange(searchState) {
    clearTimeout(this.debouncedSetState)
    this.debouncedSetState = setTimeout(() => {
      window.history.pushState(
        searchState,
        null,
        searchStateToUrl(this.props, searchState),
      )
    }, updateAfter)
    this.setState({ searchState })
  }

  toggleCategory(key) {
    this.setState((prev) => ({
      enabledCategories: {
        ...prev.enabledCategories,
        [key]: !prev.enabledCategories[key],
      },
    }))
  }

  toggleCategoryHeaders() {
    this.setState((prev) => ({
      showCategoryHeaders: !prev.showCategoryHeaders,
    }))
  }

  render() {
    const { showSectionSelectionModal: playCb } = this.props
    const categoryFiltersValue = {
      enabled: this.state.enabledCategories,
      onToggle: this.toggleCategory,
      showCategoryHeaders: this.state.showCategoryHeaders,
      onToggleCategoryHeaders: this.toggleCategoryHeaders,
    }
    return (
      <DocumentTitle title='Search Results - Plickers'>
        <div className='page searchResults page--wideLayout'>
          <div className='page-leftPanel' />
          <SearchProviderContainer
            searchState={this.state.searchState}
            onSearchStateChange={this.onSearchStateChange}
            createURL={createURL}
            hitsPerPage={100}
          >
            <RouterHistoryBridge>
              <CategoryFiltersContext.Provider value={categoryFiltersValue}>
                <ActiveResultProvider>
                  <div className='page-centerPanel'>
                    <ResultsPageHeader />
                    <div className='searchResults-content'>
                      <LibrarySection play={playCb} />
                    </div>
                    <div className='searchResults-shortcutHelp'>
                      <span className='searchResults-shortcutHelp-item'>
                        <kbd>↑↓</kbd>
                        {' navigate'}
                      </span>
                      <span className='searchResults-shortcutHelp-item'>
                        <kbd>Enter</kbd>
                        {' preview'}
                      </span>
                      <span className='searchResults-shortcutHelp-item'>
                        <kbd>Shift+Enter</kbd>
                        {' open'}
                      </span>
                      <span className='searchResults-shortcutHelp-item'>
                        <kbd>⌘K</kbd>
                        {' back to search'}
                      </span>
                    </div>
                  </div>
                  <div className='page-rightPanel'>
                    <ResultsRightPanel />
                  </div>
                </ActiveResultProvider>
              </CategoryFiltersContext.Provider>
            </RouterHistoryBridge>
          </SearchProviderContainer>
        </div>
      </DocumentTitle>
    )
  }
}

export default withRouter(connect(
  (state) => ({
    permissions: state.permissions,
  }),
  { showSectionSelectionModal },
)(SearchResultsContainer))
